import { Component, HostBinding, HostListener, Injector, Renderer2, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SettingsCountingMenuBaseComponent } from '@rift/components/settings/counting/base/Settings.Counting.MenuBase.Component';
import { UnitsOfMeasurementInputValidators } from '@rift/directives/unitsofmeasurementinput/UnitsOfMeasurementInput.Directive';
import { GlobalHeightFilterModel } from '@rift/models/restapi/GlobalHeightFilter.Model';
import { HeightService } from '@rift/service/data/height/Height.Service';
import { PleaseWaitDialogComponent } from '@shared/component/dialog/pleasewait/PleaseWait.Dialog.Component';
import { LocalStorage } from '@shared/decorator/WebStorage.Decorator';
import { ILoadDate } from '@shared/interface/ILoadData';
import { ISaveAllChanges } from '@shared/interface/ISaveAllChanges';
import { ProcessMonitorServiceProcess } from '@shared/service/processmonitor/ProcessMonitor.Service.Process';
import { IPosition } from 'angular2-draggable';
import { ISize } from 'angular2-draggable/lib/models/size';
import { Observable, of, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'rift-settings-counting-global-height-filter',
    templateUrl: './Settings.Counting.GlobalHeightFilter.Component.html',
    styleUrls: ['./Settings.Counting.GlobalHeightFilter.Component.scss'],
})
export class SettingsCountingGlobalHeightFilterComponent extends SettingsCountingMenuBaseComponent implements ILoadDate, ISaveAllChanges {
    public static className: string = 'SettingsCountingGlobalHeightFilterComponent';

    @Input()
    public get bounds(): HTMLElement {
        return this._bounds;
    }
    public set bounds(value: HTMLElement) {
        this._bounds = value;
        this.checkPosition();
    }

    @Input()
    public get zIndex(): number {
        return this._zIndex;
    }
    public set zIndex(value: number) {
        this._zIndex = value;
    }

    @HostBinding()
    public id: string = 'rift-settings-counting-global-height-filter';

    @LocalStorage(SettingsCountingGlobalHeightFilterComponent.className, 'position')
    public position: IPosition;

    @LocalStorage(SettingsCountingGlobalHeightFilterComponent.className, 'show')
    public show: boolean;

    public size: ISize;

    public form: FormGroup = null;
    public globalHeightFilter: GlobalHeightFilterModel;
    public formValuesChangeProcess: ProcessMonitorServiceProcess;

    public constructor(
        private readonly _render: Renderer2,
        private readonly _heightService: HeightService,
        private readonly _formBuilder: FormBuilder,
        private readonly _dialog: MatDialog,
        private readonly _injector: Injector) {
        super(_render, _injector, _dialog);

        this.formValuesChangeProcess = this.processMonitorService.getProcess(SettingsCountingGlobalHeightFilterComponent.className, 'Form values change');
        this.loadDataProcess = this.processMonitorService.getProcess(SettingsCountingGlobalHeightFilterComponent.className, this.loadDataProcessText);
        this.saveAllChangesProcess = this.processMonitorService.getProcess(SettingsCountingGlobalHeightFilterComponent.className, this.saveAllChangesProcessText);

        this.minWidth = 450;
        this.minHeight = 200;

        this.form = this._formBuilder.group({
            lowerBound: ['', Validators.compose([Validators.required, UnitsOfMeasurementInputValidators.min(), UnitsOfMeasurementInputValidators.max()])],
            upperBound: ['', Validators.compose([Validators.required, UnitsOfMeasurementInputValidators.min(), UnitsOfMeasurementInputValidators.max()])],
        });
        this.formGroupTracker.track(this.form);

        this.addSubscription(
            this.observableHandlerBase(this.form.valueChanges, this.formValuesChangeProcess).subscribe(() => {
                if (!this.isNullOrUndefined(this.globalHeightFilter)) {
                    this.globalHeightFilter.lowerBound = parseInt(this.form.controls.lowerBound.value, 0);
                    this.globalHeightFilter.upperBound = parseInt(this.form.controls.upperBound.value, 0);
                }
            })
            , this.formValuesChangeProcess);

        this.initConnectionState();
    }

    @HostListener('window:beforeunload')
    public deactivate(): Observable<boolean> {
        return this.deactivateBase(this);
    }

    public get hasChanges(): boolean {
        return this.hasChangesBase;
    }

    public get isValid(): boolean {
        return this.show ? this.isValidBase : true;
    }

    public loadData(pleaseWaitDialogRef?: MatDialogRef<PleaseWaitDialogComponent>, process?: ProcessMonitorServiceProcess): Observable<boolean> {
        const loadDataSub = zip(
            this._heightService.getGlobalHeightFilter(process).pipe(
                map(result => {
                    if (!this.isNullOrUndefined(result)) {
                        this.globalHeightFilter = result;
                        this.changeTracker.track(this.globalHeightFilter);
                        this.setFormValues(this.globalHeightFilter);
                    }
                    return true;
                })
            ),
        );

        return this.loadDataBase(this, loadDataSub, pleaseWaitDialogRef, process);
    }

    public onSaveClick(): void {
        this.saveAllChangesStartBase(this, this.openPleaseWaitSavingDialog());
    }

    public open(): void {
        super.open();
        this.loadDataStartBase(this);
    }

    public reset(): void {
        this.changeTracker.clearChanges();
        this.globalHeightFilter = null;
        this.form.reset();
        this.changeTracker.clear();
    }

    public saveAllChanges(pleaseWaitDialogRef?: MatDialogRef<PleaseWaitDialogComponent>, process?: ProcessMonitorServiceProcess): Observable<boolean> {
        const saveAllSub = zip(
            this._heightService.updateGlobalHeightFilter(this.globalHeightFilter, process).pipe(
                map(() => true)
            )
        );

        return super.saveAllChangesBase(this, saveAllSub, pleaseWaitDialogRef, process);
    }

    public showSaveChangesWarning(): Observable<boolean> {
        return this.showSaveChangesWarningBase(this, () => {
            this._heightService.clearCache();
            return of(true);
        });
    }

    protected offline(): void {
        super.offline();
        this.loadDataStartBase(this);
    }

    protected online(): void {
        super.online();
        this.loadDataStartBase(this);
    }

    protected setReadOnly(): void {
        super.setReadOnly();
        this.form.disable();
    }

    protected setReadWrite(): void {
        super.setReadWrite();
        this.form.enable();
    }

    private setFormValues(globalHeightFilter: GlobalHeightFilterModel): void {
        this.form.controls.lowerBound.setValue(globalHeightFilter.lowerBound, { emitEvent: false });
        this.form.controls.upperBound.setValue(globalHeightFilter.upperBound, { emitEvent: false });
    }
}
